/**
 * Kommentarspalte rechts neben der Mail — wie in Word: oben das Feld für
 * einen neuen Kommentar (allgemein oder zu einer markierten Stelle), für
 * Ansprechpersonen „Passt so“, darunter die Fäden. Auf breiten Bildschirmen
 * steht jede Karte auf Höhe ihrer Stelle in der Mail, die gewählte Karte
 * genau daneben (mit Verbindungslinie); Einträge ohne Stelle („Passt so“,
 * Kommentare zur ganzen Mail) stehen darunter. Schmal: einfache Liste.
 * Ohne `adapter.senden` nur lesen: kein Feld, kein „Passt so“, keine Antworten.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { CircleCheckBig, Lock, MessageSquarePlus, MessagesSquare, MousePointer2, PenTool, Reply, Send, ThumbsUp, X } from 'lucide-react';
import { Avatar } from '../Planung/Avatar';
import type { FeedbackEintrag, PlanungsDetail, WerkstattAdapter } from '../../lib/planung';
import { AenderungKarte } from './AenderungKarte';
import { EintragAktionen, EintragKopf, EintragStatus } from './Teile';
import {
  ART_LABEL, ENTWURF_ID, bewegungReduziert, eintragOffen, kuerzen, stiftFarbe, zurueckziehbar,
  type Breite, type Entwurf, type WerkstattAktionen,
} from './werkstattHilfen';

type Filter = 'offen' | 'alle' | 'erledigt';

/** Abstand zwischen zwei Karten (px). */
const LUECKE = 10;

type Props = {
  mail: PlanungsDetail;
  adapter: WerkstattAdapter;
  aktionen: WerkstattAktionen;
  breite: Breite;
  /** Karten auf Höhe ihrer Stelle (zweispaltig) */
  ausgerichtet: boolean;
  /** Abstand der Spalte zur Mail (für die Verbindungslinie) */
  luecke: number;
  /** Höhe je id ab Oberkante der Bühne */
  positionen: Map<string, number>;
  /** Die Bühne, auf die sich die Positionen beziehen */
  bezugRef: RefObject<HTMLElement | null>;
  aktivId: string | null;
  onWaehlen: (id: string) => void;
  onHover: (id: string | null) => void;
  entwurf: Entwurf | null;
  onEntwurfVerwerfen: () => void;
  onKommentiert: (id: string) => void;
  onBreite: (b: Breite) => void;
  onZeigen: (id: string) => void;
  /** „Stelle in der Mail wählen“ (Modus Kommentieren) */
  onStelleWaehlen?: () => void;
  /** Erhöhen → Textfeld bekommt den Fokus */
  fokus: number;
  /** Text des neuen Kommentars (liegt in der Werkstatt, damit er beim Reiterwechsel bleibt) */
  text: string;
  onText: (t: string) => void;
  /** Markierungen, die sich in dieser Ansicht nicht an ihre Stelle zeichnen lassen */
  markierungOhneOrt: ReadonlySet<string>;
};

/**
 * Karten untereinander ohne Überlappung, möglichst auf ihrer Wunschhöhe.
 * Die aktive Karte steht genau an ihrer Stelle; Karten davor weichen nach oben aus.
 */
function anordnen(wunsch: (number | null)[], h: number[], aktiv: number): number[] {
  const n = wunsch.length;
  const top = new Array<number>(n).fill(0);
  let unten = 0;
  for (let i = 0; i < n; i++) {
    top[i] = Math.max(wunsch[i] ?? unten, unten);
    unten = top[i] + h[i] + LUECKE;
  }
  const w = aktiv >= 0 ? wunsch[aktiv] : null;
  if (w !== null && w !== undefined) {
    top[aktiv] = Math.max(0, w);
    for (let i = aktiv - 1; i >= 0; i--) top[i] = Math.min(top[i], top[i + 1] - LUECKE - h[i]);
    for (let i = aktiv + 1; i < n; i++) top[i] = Math.max(wunsch[i] ?? 0, top[i - 1] + h[i - 1] + LUECKE);
    if (top[0] < 0) {
      top[0] = 0;
      for (let i = 1; i < n; i++) top[i] = Math.max(top[i], top[i - 1] + h[i - 1] + LUECKE);
    }
  }
  return top;
}

function gleicheTops(a: Record<string, number>, b: Record<string, number>): boolean {
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => Math.abs(a[k] - (b[k] ?? NaN)) < 0.5);
}

export function KommentarSpalte(props: Props) {
  const {
    mail, adapter, aktionen, breite, ausgerichtet, luecke, positionen, bezugRef, aktivId,
    onWaehlen, onHover, entwurf, onEntwurfVerwerfen, onKommentiert, onBreite, onZeigen, onStelleWaehlen, fokus,
    text, onText: setText, markierungOhneOrt,
  } = props;
  const [filter, setFilter] = useState<Filter>('offen');
  const [laeuft, setLaeuft] = useState(false);

  const { oberste, antwortenVon } = useMemo(() => {
    const ids = new Set(mail.feedback.map((f) => f.id));
    const oben: FeedbackEintrag[] = [];
    const antw = new Map<string, FeedbackEintrag[]>();
    for (const f of mail.feedback) {
      if (f.antwort_auf && ids.has(f.antwort_auf)) {
        const l = antw.get(f.antwort_auf) ?? [];
        l.push(f);
        antw.set(f.antwort_auf, l);
      } else oben.push(f);
    }
    for (const l of antw.values()) l.sort((a, b) => a.created_at.localeCompare(b.created_at));
    return { oberste: oben, antwortenVon: antw };
  }, [mail.feedback]);

  // Offen: Kommentare und Vorschläge ohne Ergebnis — „Passt so“ steht nur unter „Alle“
  const zahl = useMemo(() => ({
    offen: oberste.filter(eintragOffen).length,
    alle: oberste.length,
    erledigt: oberste.filter((f) => !!f.ergebnis).length,
  }), [oberste]);

  // Sichtbare Fäden: allgemeine oben (älteste zuerst), dann nach Stelle in der Mail (ausgerichtet: allgemeine darunter)
  const faeden = useMemo(() => {
    const l = oberste.filter((f) => (filter === 'alle' ? true : filter === 'offen' ? eintragOffen(f) : !!f.ergebnis));
    return l.sort((a, b) => {
      const ya = positionen.get(a.id);
      const yb = positionen.get(b.id);
      if (ya === undefined && yb === undefined) return a.created_at.localeCompare(b.created_at);
      if (ya === undefined) return -1;
      if (yb === undefined) return 1;
      return ya - yb || a.created_at.localeCompare(b.created_at);
    });
  }, [oberste, filter, positionen]);

  const entwurfImFluss = ausgerichtet && !!entwurf && positionen.has(ENTWURF_ID);
  // Ausgerichtet: nur Einträge mit Stelle stehen neben der Mail, die übrigen darunter
  const mitOrt = useMemo(() => (ausgerichtet ? faeden.filter((f) => positionen.has(f.id)) : faeden), [ausgerichtet, faeden, positionen]);
  const ohneOrt = useMemo(() => (ausgerichtet ? faeden.filter((f) => !positionen.has(f.id)) : []), [ausgerichtet, faeden, positionen]);

  async function kommentarSenden() {
    const t = text.trim();
    if (!t || laeuft) return;
    setLaeuft(true);
    const id = await aktionen.senden({
      art: 'kommentar',
      text: t,
      zitat: entwurf?.zitat ?? null,
      anker: entwurf?.anker ?? null,
      markierung: entwurf?.markierung ?? null,
    }, entwurf ? 'Kommentar zur Stelle gespeichert.' : 'Kommentar gespeichert.');
    setLaeuft(false);
    if (id) {
      setText('');
      if (filter === 'erledigt') setFilter('offen');
      onKommentiert(id);
    }
  }

  const verfasser = (imFluss: boolean) => (
    <Verfasser
      ichName={adapter.ichName}
      art={adapter.art}
      entwurf={entwurf}
      text={text}
      setText={setText}
      laeuft={laeuft}
      onSenden={kommentarSenden}
      onVerwerfen={onEntwurfVerwerfen}
      onAbbrechen={() => { setText(''); onEntwurfVerwerfen(); }}
      onStelleWaehlen={onStelleWaehlen}
      fokus={fokus}
      imFluss={imFluss}
      ausgerichtet={ausgerichtet}
    />
  );

  // ── Ausrichten neben der Mail ────────────────────────────────────────────

  const listeRef = useRef<HTMLDivElement>(null);
  const kartenRef = useRef(new Map<string, HTMLDivElement>());
  const [tops, setTops] = useState<Record<string, number>>({});
  const [listenHoehe, setListenHoehe] = useState(0);
  const [versatz, setVersatz] = useState(0);
  const [takt, setTakt] = useState(0);
  const spalteRef = useRef<HTMLDivElement>(null);

  const plaetze = useMemo(() => {
    const l: { key: string; y: number | null }[] = mitOrt.map((f) => ({ key: f.id, y: positionen.get(f.id) ?? null }));
    if (entwurfImFluss) {
      const y = positionen.get(ENTWURF_ID) ?? null;
      const i = l.findIndex((p) => p.y !== null && y !== null && p.y > y);
      l.splice(i < 0 ? l.length : i, 0, { key: ENTWURF_ID, y });
    }
    return l;
  }, [mitOrt, positionen, entwurfImFluss]);

  const fokusKey = entwurfImFluss ? ENTWURF_ID : aktivId;

  useLayoutEffect(() => {
    if (!ausgerichtet) return;
    const liste = listeRef.current;
    const bezug = bezugRef.current;
    if (!liste || !bezug) return;
    const v = liste.getBoundingClientRect().top - bezug.getBoundingClientRect().top;
    const hs = plaetze.map((p) => kartenRef.current.get(p.key)?.offsetHeight ?? 0);
    const wunsch = plaetze.map((p) => (p.y === null ? null : Math.max(0, p.y - v)));
    const t = anordnen(wunsch, hs, plaetze.findIndex((p) => p.key === fokusKey));
    const neu: Record<string, number> = {};
    plaetze.forEach((p, i) => { neu[p.key] = Math.round(t[i]); });
    const h = plaetze.length ? Math.max(...t.map((x, i) => x + hs[i])) : 0;
    if (!gleicheTops(neu, tops)) setTops(neu);
    if (Math.abs(h - listenHoehe) > 0.5) setListenHoehe(h);
    if (Math.abs(v - versatz) > 0.5) setVersatz(v);
  }, [ausgerichtet, bezugRef, plaetze, fokusKey, tops, listenHoehe, versatz, takt]);

  useEffect(() => {
    if (!ausgerichtet || typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver(() => setTakt((n) => n + 1));
    kartenRef.current.forEach((el) => ro.observe(el));
    // Feld oben wächst → Liste rückt nach unten
    if (spalteRef.current) ro.observe(spalteRef.current);
    const fenster = () => setTakt((n) => n + 1);
    window.addEventListener('resize', fenster);
    return () => { ro.disconnect(); window.removeEventListener('resize', fenster); };
  }, [ausgerichtet, plaetze]);

  // Gewählte Karte in der schmalen Liste sichtbar machen
  useEffect(() => {
    if (ausgerichtet || !aktivId) return;
    const el = kartenRef.current.get(aktivId);
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.top < 80 || r.bottom > window.innerHeight - 20) el.scrollIntoView({ block: 'nearest', behavior: bewegungReduziert() ? 'auto' : 'smooth' });
  }, [aktivId, ausgerichtet]);

  // Ausgerichtet: gewählte Karte bzw. das Kommentarfeld nach dem Anordnen sichtbar
  // machen — über einen Platzhalter an der Zielhöhe (die Karte selbst gleitet noch)
  const zielRef = useRef<HTMLDivElement>(null);
  const gezeigt = useRef<string | null>(null);
  useEffect(() => {
    if (!ausgerichtet || !fokusKey) { gezeigt.current = null; return; }
    if (gezeigt.current === fokusKey) return;
    const el = kartenRef.current.get(fokusKey);
    const ziel = zielRef.current;
    const top = tops[fokusKey];
    let probe: HTMLElement | null = null;
    if (el && top !== undefined && ziel) {
      ziel.style.top = `${top}px`;
      ziel.style.height = `${el.offsetHeight}px`;
      probe = ziel;
    } else if (el && ohneOrt.some((f) => f.id === fokusKey)) probe = el;
    if (!probe) return;
    gezeigt.current = fokusKey;
    const r = probe.getBoundingClientRect();
    if (r.top < 80 || r.bottom > window.innerHeight - 20) probe.scrollIntoView({ block: 'nearest', behavior: bewegungReduziert() ? 'auto' : 'smooth' });
  }, [ausgerichtet, fokusKey, tops, ohneOrt]);

  const merken = (key: string) => (el: HTMLDivElement | null) => {
    if (el) kartenRef.current.set(key, el);
    else kartenRef.current.delete(key);
  };

  // Verbindungslinie der gewählten Karte zur Stelle in der Mail
  const linie = (() => {
    if (!ausgerichtet || !fokusKey) return null;
    const y = positionen.get(fokusKey);
    const top = tops[fokusKey];
    if (y === undefined || top === undefined) return null;
    const ay = Math.max(0, y - versatz) + 10;
    const ky = top + 22;
    const oben = Math.min(ay, ky) - 6;
    const hoehe = Math.abs(ay - ky) + 12;
    const mx = luecke / 2;
    return (
      <svg className="za-verbinder" style={{ top: oben, left: -luecke, width: luecke + 2, height: hoehe }} aria-hidden="true">
        <path d={`M3 ${ay - oben} H${mx} V${ky - oben} H${luecke + 2}`} />
        <circle cx="3" cy={ay - oben} r="3" />
      </svg>
    );
  })();

  const faden = (f: FeedbackEintrag) => (
    <Faden
      f={f}
      alle={mail.feedback}
      markierungOhneOrt={markierungOhneOrt.has(f.id)}
      antworten={antwortenVon.get(f.id) ?? []}
      mail={mail}
      adapter={adapter}
      aktionen={aktionen}
      aktiv={f.id === aktivId}
      breite={breite}
      onWaehlen={onWaehlen}
      onHover={onHover}
      onBreite={onBreite}
      onZeigen={onZeigen}
    />
  );

  const leer = faeden.length === 0 && !entwurfImFluss;
  const nurLesen = !adapter.senden;

  return (
    <div ref={spalteRef} className={`za-spalte${ausgerichtet ? ' za-spalte--ausgerichtet' : ''}`}>
      {adapter.art === 'link' && !nurLesen && <PasstKarte mail={mail} aktionen={aktionen} />}

      {nurLesen ? (
        <p className="za-spalte-hinweis za-spalte-hinweis--lesen" role="note">
          <Lock size={14} aria-hidden="true" />
          <span>{adapter.nurLesenHinweis ?? 'Hier kannst Du die Rückmeldungen nur lesen.'}</span>
        </p>
      ) : entwurfImFluss ? (
        <p className="za-spalte-hinweis">
          <MessageSquarePlus size={14} aria-hidden="true" /> Dein Kommentar steht neben der Stelle in der Mail.
        </p>
      ) : verfasser(false)}

      <div className="za-filter">
        <div className="seg-control seg-control--sm" role="group" aria-label="Rückmeldungen filtern">
          {(['offen', 'alle', 'erledigt'] as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              className={`seg-btn${filter === f ? ' active' : ''}`}
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
            >
              {f === 'offen' ? 'Offen' : f === 'alle' ? 'Alle' : 'Erledigt'}
              <span className="za-zahl">{zahl[f]}</span>
            </button>
          ))}
        </div>
      </div>

      {leer ? (
        <div className="za-leer">
          <MessagesSquare size={22} aria-hidden="true" />
          <p className="za-leer-titel">
            {filter === 'erledigt' ? 'Noch nichts erledigt' : filter === 'offen' && zahl.alle ? 'Keine offenen Rückmeldungen' : 'Noch keine Rückmeldungen'}
          </p>
          <p className="za-leer-text">
            {filter === 'erledigt'
              ? 'Erledigte Kommentare und entschiedene Vorschläge erscheinen hier.'
              : filter === 'offen' && zahl.alle
                ? 'Alles abgearbeitet. Unter „Alle“ stehen die bisherigen Kommentare.'
                : nurLesen
                  ? 'Sobald jemand kommentiert, steht es hier.'
                  : 'Markiere eine Stelle in der Mail oder schreib oben einen Kommentar zur ganzen Mail.'}
          </p>
        </div>
      ) : ausgerichtet ? (
        <>
          {plaetze.length > 0 && (
            <div ref={listeRef} className="za-faeden za-faeden--ausgerichtet" style={{ height: listenHoehe }}>
              {linie}
              <div ref={zielRef} className="za-platz-ziel" aria-hidden="true" />
              {plaetze.map((p) => (
                <div
                  key={p.key}
                  ref={merken(p.key)}
                  className={`za-platz${p.key === fokusKey ? ' za-platz--fokus' : ''}`}
                  style={{ transform: `translateY(${tops[p.key] ?? Math.max(0, (p.y ?? 0) - versatz)}px)` }}
                >
                  {p.key === ENTWURF_ID ? verfasser(true) : faden(mitOrt.find((f) => f.id === p.key)!)}
                </div>
              ))}
            </div>
          )}
          {ohneOrt.length > 0 && (
            <section className="za-faeden za-faeden--ohne-ort" aria-label="Zur ganzen Mail">
              <p className="za-faeden-titel">Zur ganzen Mail</p>
              {ohneOrt.map((f) => (
                <div key={f.id} ref={merken(f.id)} className="za-platz-liste">{faden(f)}</div>
              ))}
            </section>
          )}
        </>
      ) : (
        <div className="za-faeden">
          {faeden.map((f) => (
            <div key={f.id} ref={merken(f.id)} className="za-platz-liste">{faden(f)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── „Passt so“ (Ansprechpersonen) ───────────────────────────────────────────

function PasstKarte({ mail, aktionen }: { mail: PlanungsDetail; aktionen: WerkstattAktionen }) {
  const [laeuft, setLaeuft] = useState(false);
  const gesagt = mail.meine_anfrage?.antwort === 'passt' || mail.feedback.some((f) => f.art === 'passt' && f.ist_meins);

  if (gesagt) {
    return (
      <section className="za-passt za-passt--gesagt" aria-label="Deine Antwort">
        <CircleCheckBig size={22} aria-hidden="true" className="za-passt-symbol" />
        <div>
          <p className="za-passt-titel">Du hast „Passt so“ gesagt</p>
          <p className="za-passt-text">Danke! Fällt Dir doch noch etwas auf, schreib einfach einen Kommentar.</p>
        </div>
      </section>
    );
  }
  return (
    <section className="za-passt" aria-label="Passt die Mail so?">
      <div>
        <p className="za-passt-titel">Passt die Mail so?</p>
        <p className="za-passt-text">Wenn alles stimmt, gib kurz Bescheid. Sonst kommentier oder ändere einfach den Text.</p>
      </div>
      <button
        type="button"
        className="za-passt-knopf"
        disabled={laeuft}
        onClick={async () => {
          setLaeuft(true);
          await aktionen.senden({ art: 'passt' }, 'Danke! Das Team sieht, dass die Mail für Dich passt.');
          setLaeuft(false);
        }}
      >
        {laeuft ? <span className="spinner za-spinner-klein" aria-hidden="true" /> : <ThumbsUp size={18} aria-hidden="true" />}
        Passt so
      </button>
    </section>
  );
}

// ── Neuer Kommentar ─────────────────────────────────────────────────────────

type VerfasserProps = {
  ichName: string;
  art: WerkstattAdapter['art'];
  entwurf: Entwurf | null;
  text: string;
  setText: (t: string) => void;
  laeuft: boolean;
  onSenden: () => void;
  /** Bezug zur Stelle entfernen (Text bleibt) */
  onVerwerfen: () => void;
  /** Kommentar ganz verwerfen */
  onAbbrechen: () => void;
  onStelleWaehlen?: () => void;
  fokus: number;
  imFluss: boolean;
  ausgerichtet: boolean;
};

function Verfasser({ ichName, art, entwurf, text, setText, laeuft, onSenden, onVerwerfen, onAbbrechen, onStelleWaehlen, fokus, imFluss, ausgerichtet }: VerfasserProps) {
  const feld = useRef<HTMLTextAreaElement>(null);
  const ersterFokus = useRef(fokus);

  useEffect(() => {
    if (fokus === ersterFokus.current && !imFluss) return;
    const el = feld.current;
    if (!el) return;
    if (!ausgerichtet) el.scrollIntoView({ block: 'center', behavior: bewegungReduziert() ? 'auto' : 'smooth' });
    el.focus({ preventScroll: true });
  }, [fokus, imFluss, ausgerichtet]);

  // Feld wächst mit dem Text
  useLayoutEffect(() => {
    const el = feld.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, imFluss ? 64 : 72), 260)}px`;
  }, [text, imFluss]);

  const bezug = entwurf && (entwurf.zitat || entwurf.anker || entwurf.markierung);

  return (
    <form
      className={`za-verfasser${imFluss ? ' za-verfasser--fluss' : ''}${bezug ? ' za-verfasser--bezug' : ''}`}
      onSubmit={(e) => { e.preventDefault(); onSenden(); }}
      aria-label={bezug ? 'Kommentar zur markierten Stelle' : 'Neuer Kommentar'}
    >
      <div className="za-verfasser-kopf">
        <Avatar name={ichName} groesse="sm" />
        <span className="za-kopf-name">{ichName}</span>
      </div>
      {bezug && (
        <div className="za-bezug">
          <div className="za-bezug-inhalt">
            {entwurf.zitat ? (
              <blockquote className="za-zitat">„{kuerzen(entwurf.zitat, 180)}“</blockquote>
            ) : entwurf.markierung ? (
              <p className="za-stelle">
                <span className="za-markierung-punkt" style={{ background: stiftFarbe(entwurf.markierung.farbe) }} aria-hidden="true" />
                <PenTool size={13} aria-hidden="true" /> Deine Markierung in der Mail
              </p>
            ) : entwurf.anker ? (
              <p className="za-stelle"><MousePointer2 size={13} aria-hidden="true" /> Stelle: „{kuerzen(entwurf.anker.text, 90)}“</p>
            ) : null}
          </div>
          <button type="button" className="za-icon-knopf" onClick={onVerwerfen} aria-label="Bezug zur Stelle entfernen" title="Bezug entfernen">
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      )}
      <textarea
        ref={feld}
        className="za-feld"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); onSenden(); }
          if (e.key === 'Escape' && bezug) { e.preventDefault(); e.stopPropagation(); onAbbrechen(); }
        }}
        placeholder={bezug ? 'Was möchtest Du zu dieser Stelle sagen?' : art === 'link' ? 'Was möchtest Du zur Mail sagen?' : 'Kommentar zur ganzen Mail …'}
        aria-label="Kommentar"
        maxLength={4000}
        rows={2}
      />
      <div className="za-verfasser-fuss">
        {!bezug && onStelleWaehlen ? (
          <button type="button" className="za-link-knopf" onClick={onStelleWaehlen}>
            <MousePointer2 size={13} aria-hidden="true" /> Stelle in der Mail wählen
          </button>
        ) : <span className="za-tastenhinweis" aria-hidden="true">Strg + Enter</span>}
        <div className="za-verfasser-knoepfe">
          {bezug && <button type="button" className="btn btn-ghost btn-sm" onClick={onAbbrechen}>Abbrechen</button>}
          <button type="submit" className="btn btn-primary btn-sm" disabled={!text.trim() || laeuft}>
            {laeuft ? <span className="spinner za-spinner-klein" aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
            Kommentieren
          </button>
        </div>
      </div>
    </form>
  );
}

// ── Faden ───────────────────────────────────────────────────────────────────

type FadenProps = {
  f: FeedbackEintrag;
  /** alle Einträge der Mail (Zurückziehen nur ohne fremde Antworten) */
  alle: FeedbackEintrag[];
  /** Markierung lässt sich in dieser Ansicht nicht an ihre Stelle zeichnen */
  markierungOhneOrt: boolean;
  antworten: FeedbackEintrag[];
  mail: PlanungsDetail;
  adapter: WerkstattAdapter;
  aktionen: WerkstattAktionen;
  aktiv: boolean;
  breite: Breite;
  onWaehlen: (id: string) => void;
  onHover: (id: string | null) => void;
  onBreite: (b: Breite) => void;
  onZeigen: (id: string) => void;
};

function Faden({ f, alle, markierungOhneOrt, antworten, mail, adapter, aktionen, aktiv, breite, onWaehlen, onHover, onBreite, onZeigen }: FadenProps) {
  const [antwortOffen, setAntwortOffen] = useState(false);
  const [antwort, setAntwort] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const feld = useRef<HTMLTextAreaElement>(null);
  const darfAntworten = !!adapter.senden;
  const zeigeAntwort = darfAntworten && (antwortOffen || (aktiv && !f.ergebnis));

  async function antworten_() {
    const t = antwort.trim();
    if (!t || laeuft) return;
    setLaeuft(true);
    const id = await aktionen.senden({ art: 'kommentar', text: t, antwort_auf: f.id }, 'Antwort gespeichert.');
    setLaeuft(false);
    if (id) { setAntwort(''); setAntwortOffen(false); }
  }

  const mk = f.markierung;
  const andereAnsicht = mk && mk.ansicht !== breite;

  return (
    <article
      className={`za-karte za-karte--${f.art}${aktiv ? ' za-karte--aktiv' : ''}${f.ergebnis ? ' za-karte--erledigt' : ''}`}
      aria-label={`${ART_LABEL[f.art]} von ${f.autor_name}`}
      aria-current={aktiv ? 'true' : undefined}
      tabIndex={0}
      onClick={() => onWaehlen(f.id)}
      onMouseEnter={() => onHover(f.id)}
      onMouseLeave={() => onHover(null)}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onWaehlen(f.id); }
      }}
    >
      <EintragKopf eintrag={f} adapter={adapter} />

      {f.zitat && <blockquote className="za-zitat">„{f.zitat}“</blockquote>}
      {!f.zitat && f.anker && !mk && <p className="za-stelle"><MousePointer2 size={13} aria-hidden="true" /> Stelle: „{kuerzen(f.anker.text, 80)}“</p>}
      {mk && markierungOhneOrt && !andereAnsicht ? (
        <div className="za-markierung-info">
          <span className="za-markierung-punkt" style={{ background: stiftFarbe(mk.farbe) }} aria-hidden="true" />
          <span>Markierung in einem anders breiten Fenster gezeichnet — hier lässt sie sich nicht genau zeigen.</span>
        </div>
      ) : mk && (
        <div className="za-markierung-info">
          <span className="za-markierung-punkt" style={{ background: stiftFarbe(mk.farbe) }} aria-hidden="true" />
          <span>Markierung in der Mail</span>
          <button
            type="button"
            className="za-link-knopf"
            onClick={(e) => {
              e.stopPropagation();
              if (andereAnsicht) onBreite(mk.ansicht);
              onWaehlen(f.id);
              onZeigen(f.id);
            }}
          >
            {andereAnsicht ? `In der ${mk.ansicht === 'handy' ? 'Handy' : 'Computer'}-Ansicht zeigen` : 'Zeigen'}
          </button>
        </div>
      )}

      {f.art === 'vorschlag'
        ? <AenderungKarte eintrag={f} mail={mail} adapter={adapter} aktionen={aktionen} />
        : f.text && <p className="za-text">{f.text}</p>}
      {f.art === 'passt' && !f.text && (
        <p className="za-passt-zeile"><ThumbsUp size={14} aria-hidden="true" /> Für {(f.autor_name ?? '').split(/\s+/)[0]} passt die Mail so.</p>
      )}

      <EintragStatus eintrag={f} />

      {antworten.length > 0 && (
        <ol className="za-antworten" aria-label="Antworten">
          {antworten.map((a) => (
            <li key={a.id} className="za-antwort">
              <EintragKopf eintrag={a} adapter={adapter} klein />
              {a.text && <p className="za-text">{a.text}</p>}
              {adapter.zurueckziehen && zurueckziehbar(a, alle) && (
                <button type="button" className="za-link-knopf za-link-knopf--leise" onClick={(e) => { e.stopPropagation(); aktionen.zurueckziehen(a); }}>
                  Zurückziehen
                </button>
              )}
            </li>
          ))}
        </ol>
      )}

      {zeigeAntwort ? (
        <form
          className="za-antwort-feld"
          onSubmit={(e) => { e.preventDefault(); void antworten_(); }}
          onClick={(e) => e.stopPropagation()}
        >
          <textarea
            ref={feld}
            className="za-feld za-feld--klein"
            value={antwort}
            onChange={(e) => setAntwort(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void antworten_(); }
              if (e.key === 'Escape') { e.stopPropagation(); setAntwortOffen(false); (e.currentTarget as HTMLTextAreaElement).blur(); }
            }}
            placeholder="Antworten …"
            aria-label={`Antwort an ${f.autor_name}`}
            maxLength={4000}
            rows={1}
          />
          {(antwort.trim() || antwortOffen) && (
            <div className="za-antwort-knoepfe">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setAntwort(''); setAntwortOffen(false); }}>Abbrechen</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={!antwort.trim() || laeuft}>
                {laeuft && <span className="spinner za-spinner-klein" aria-hidden="true" />}
                Antworten
              </button>
            </div>
          )}
        </form>
      ) : null}

      <EintragAktionen eintrag={f} alle={alle} adapter={adapter} aktionen={aktionen}>
        {darfAntworten && !zeigeAntwort && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              setAntwortOffen(true);
              window.requestAnimationFrame(() => feld.current?.focus());
            }}
          >
            <Reply size={14} aria-hidden="true" /> Antworten
          </button>
        )}
      </EintragAktionen>
    </article>
  );
}
